/**
 * Run-view derivations: elapsed, ETA and the sub-status line.
 *
 * Nothing here invents a number. The ETA needs at least two finished stages
 * before it means anything, and returns null otherwise so callers render `—`.
 */

import type { DwiStage, JobStatus, Modality, PwiStage, StageStatus } from "@/ipc/contract";
import type { StageTiming } from "./timings";
import { timingKey } from "./timings";

/**
 * Relative cost per stage, used to project an ETA.
 *
 * DWI is **measured**: a full `sub-02e8eb42` run on an RTX A5000 took 218 s —
 * prepdata 0.8, gen_mask 5.1, skull_strip 0.3, registration 104.4,
 * inference 65.0, report 42.3. Registration and inference are 80% of the run,
 * so a mean-of-completed-stages estimate is useless: after the first three
 * stages the mean is 2.1 s and the UI would promise "6 seconds remaining" with
 * 211 s left to go.
 *
 * PWI is **estimated**, not measured — no PWI run has been timed yet.
 * prepdata carries slice-timing plus motion correction over ~60 volumes, so it
 * is assumed to be much heavier than its DWI counterpart. Replace these with
 * real numbers once a PWI run is profiled.
 */
export const STAGE_WEIGHTS: {
  DWI: Record<DwiStage, number>;
  PWI: Record<PwiStage, number>;
} = {
  DWI: {
    prepdata: 0.004,
    gen_mask: 0.023,
    skull_strip: 0.002,
    registration: 0.48,
    inference: 0.3,
    report: 0.19,
  },
  PWI: {
    prepdata: 0.12,
    gen_mask: 0.02,
    skull_strip: 0.002,
    gen_ttp: 0.06,
    registration: 0.4,
    ttpadc_coreg: 0.08,
    inference: 0.2,
    report: 0.12,
  },
};

/**
 * The same six stages without a GPU, and the shape is **inverted**.
 *
 * Measured 2026-09-20, sub-02e8eb42, local Python engine, Apple M-series, 8
 * threads: prepdata 0.2s, gen_mask 527.1s, skull_strip 0.1s, registration
 * 66.4s, inference 115.6s, report 29.2s — 738.6s total.
 *
 * SynthStrip's U-Net runs on CUDA when there is one and falls back to CPU when
 * there is not, so gen_mask goes from 2.3% of the run to 71.4%. ANTs
 * registration was CPU-bound either way and is actually *faster* here than the
 * A5000 figure. Scoring a CPU run with the GPU table would sit at 3% while
 * three quarters of the wall clock had gone — the same class of error as the
 * mean-of-completed-stages estimate this table exists to avoid, just in the
 * other direction.
 *
 * PWI is scaled from the DWI ratios, not measured; no PWI run has been timed
 * on CPU.
 */
export const STAGE_WEIGHTS_CPU: {
  DWI: Record<DwiStage, number>;
  PWI: Record<PwiStage, number>;
} = {
  DWI: {
    prepdata: 0.001,
    gen_mask: 0.714,
    skull_strip: 0.001,
    registration: 0.09,
    inference: 0.157,
    report: 0.039,
  },
  PWI: {
    prepdata: 0.06,
    gen_mask: 0.5,
    skull_strip: 0.001,
    gen_ttp: 0.03,
    registration: 0.12,
    ttpadc_coreg: 0.03,
    inference: 0.2,
    report: 0.06,
  },
};

/**
 * Which table to score a run with. `cuda` gets the measured GPU numbers;
 * everything else — local CPU, and an emulated amd64 image, which is slower
 * still but in the same proportions — gets the CPU ones.
 */
export function weightsFor(deviceClass: string | undefined) {
  return deviceClass === "cuda" ? STAGE_WEIGHTS : STAGE_WEIGHTS_CPU;
}

function stageWeight(modality: Modality, id: string, deviceClass?: string): number {
  const table: Record<string, number> = weightsFor(deviceClass)[modality];
  // An unknown id can only come from a contract change; 0.1 keeps it visible
  // in the projection without letting it dominate.
  return table[id] ?? 0.1;
}

export function elapsedSec(
  timing: StageTiming | undefined,
  now: number,
  fallbackDurationSec: number | null = null,
): number | null {
  if (!timing?.startedMs) return fallbackDurationSec;
  return ((timing.finishedMs ?? now) - timing.startedMs) / 1000;
}

export function jobElapsedSec(job: JobStatus | null, now: number): number | null {
  if (!job?.startedIso) return null;
  const start = new Date(job.startedIso).getTime();
  const end = job.finishedIso ? new Date(job.finishedIso).getTime() : now;
  return (end - start) / 1000;
}

/**
 * Weighted ETA.
 *
 * Calibrates seconds-per-unit-weight from the stages that have actually
 * finished in this run, then projects that rate over the weight still
 * outstanding — including the unfinished remainder of the running stage, which
 * matters because `registration` alone is nearly half a DWI run. The estimate
 * self-corrects on a slow or CPU-only machine as more stages land.
 *
 * Returns null until at least one stage has completed; a fabricated number is
 * worse than `—`.
 */
export function estimateEtaSec(
  job: JobStatus | null,
  timings: Record<string, StageTiming>,
  now: number,
  deviceClass?: string,
): number | null {
  if (!job || job.state !== "running") return null;

  let observedMs = 0;
  let observedWeight = 0;
  for (const stage of job.stages) {
    if (stage.state !== "completed") continue;
    const t = timings[timingKey(stage.modality, stage.id)];
    const ms =
      t?.startedMs != null && t.finishedMs != null
        ? t.finishedMs - t.startedMs
        : stage.durationSec != null
          ? stage.durationSec * 1000
          : null;
    if (ms == null) continue;
    observedMs += ms;
    observedWeight += stageWeight(stage.modality, stage.id);
  }
  if (observedWeight <= 0 || observedMs <= 0) return null;

  const msPerWeight = observedMs / observedWeight;

  let remainingMs = 0;
  for (const stage of job.stages) {
    const weight = stageWeight(stage.modality, stage.id, deviceClass);
    if (stage.state === "pending") {
      remainingMs += weight * msPerWeight;
    } else if (stage.state === "running") {
      const t = timings[timingKey(stage.modality, stage.id)];
      const spent = t?.startedMs ? now - t.startedMs : 0;
      remainingMs += Math.max(0, weight * msPerWeight - spent);
    }
  }
  return remainingMs / 1000;
}

/**
 * Weight-based completion fraction. `JobStatus.progressPct` counts stages, so a
 * DWI run sits at 50% for the whole of registration+inference; this is what the
 * progress ring shows instead.
 */
export function weightedProgressPct(
  job: JobStatus | null,
  timings: Record<string, StageTiming>,
  now: number,
  deviceClass?: string,
): number {
  if (!job || job.stages.length === 0) return 0;
  let total = 0;
  let done = 0;
  let observedMs = 0;
  let observedWeight = 0;

  for (const stage of job.stages) {
    const weight = stageWeight(stage.modality, stage.id, deviceClass);
    total += weight;
    if (stage.state === "completed" || stage.state === "skipped") {
      done += weight;
      const t = timings[timingKey(stage.modality, stage.id)];
      if (t?.startedMs != null && t.finishedMs != null) {
        observedMs += t.finishedMs - t.startedMs;
        observedWeight += weight;
      }
    }
  }

  // Credit the running stage for the fraction of its expected time spent.
  const running = job.stages.find((s) => s.state === "running");
  if (running && observedWeight > 0) {
    const msPerWeight = observedMs / observedWeight;
    const weight = stageWeight(running.modality, running.id, deviceClass);
    const t = timings[timingKey(running.modality, running.id)];
    const spent = t?.startedMs ? now - t.startedMs : 0;
    const expected = weight * msPerWeight;
    if (expected > 0) done += weight * Math.min(0.99, spent / expected);
  }

  if (job.state === "completed") return 100;
  return total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
}

export function currentStageStatus(job: JobStatus | null): StageStatus | null {
  if (!job) return null;
  return (
    job.stages.find((s) => s.state === "running") ??
    job.stages.find((s) => s.id === job.currentStage && s.modality === job.currentModality) ??
    null
  );
}

export function completedStageCount(job: JobStatus | null): number {
  return job?.stages.filter((s) => s.state === "completed").length ?? 0;
}

/**
 * The runner prefixes its structured messages with the subject id, e.g.
 * `[sub-02e8eb42] Step 2: SyN registration`. The prefix is the same on every
 * line, so it is stripped for display and shown once in the header instead.
 */
export function stripSubjectPrefix(line: string): { prefix: string | null; text: string } {
  const match = /^\s*(\[sub-[^\]]+\])\s*(.*)$/.exec(line);
  return match ? { prefix: match[1], text: match[2] } : { prefix: null, text: line };
}

/**
 * The most recent structured line, used as a sub-status under the running
 * stage — `registration` takes most of a run's wall clock, and a bar that has
 * not moved for four minutes looks hung without it.
 */
export function latestStatusLine(
  lines: { line: string; level: string }[],
  maxLookBack = 400,
): string | null {
  const start = Math.max(0, lines.length - maxLookBack);
  for (let i = lines.length - 1; i >= start; i -= 1) {
    const entry = lines[i];
    if (entry.level === "trace") continue;
    const { text } = stripSubjectPrefix(entry.line);
    const trimmed = text.trim();
    // Banner rules and separators carry no information.
    if (!trimmed || /^[=\-*_]{4,}$/.test(trimmed)) continue;
    return trimmed;
  }
  return null;
}
